import { ModuleWithProviders } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { SelectivePreloadingStrategy } from '@jepto/shared/routing/selective-preloading.strategy';

import { ResubscribeGuard } from '@jepto/features/resubscribe/guards/resubscribe.guard';
import { SubscribeGuard } from '@jepto/features/subscribe/guards/subscribe.guard';
import { AuthGuard } from '@jepto/shared/guards/auth.guard';
import { CognitoIdTokenGuard } from '@jepto/shared/guards/cognito-id-token.guard';
import { AiVisibilityFeatureGuard } from '@jepto/shared/guards/ai-visibility-feature.guard';
import { SubscriptionGuard } from '@jepto/shared/guards/subscription.guard';
export const appRoutes: Routes = [
	{
		path: '',
		redirectTo: '/get-started',
		pathMatch: 'full',
	},
	{
		path: '',
		canActivate: [AuthGuard, SubscriptionGuard],
		children: [
			{
				path: 'kpis',
				loadChildren: () =>
					import('@jepto/features/kpi-global-management/kpi-global-management.module').then(
						m => m.KPIGlobalManagementModule
					),
			},
			{
				path: 'clients',
				loadChildren: () =>
					import('@jepto/features/portfolio/portfolio.module').then(
						m => m.PortfolioModule
					),
			},
			{
				path: 'admin',
				loadChildren: () =>
					import('@jepto/features/admin/admin.module').then(
						m => m.AdminModule
					),
			},
			{
				path: 'budgets',
				loadChildren: () =>
					import('@jepto/features/budget-tracker/budget-tracker.module').then(
						m => m.BudgetTrackerModule
					),
			},
			{
				path: 'anomalies',
				loadChildren: () =>
					import('@jepto/features/anomaly/anomaly.module').then(
						m => m.AnomalyModule
					),
			},
			{
				path: 'automations',
				loadChildren: () =>
					import('@jepto/features/automation/automation.module').then(
						m => m.AutomationModule
					),
			},
			{
				path: 'insights',
				loadChildren: () =>
					import('@jepto/features/insights/insights.module').then(
						m => m.InsightsModule
					),
			},
			{
				path: 'pipelines',
				canActivate: [CognitoIdTokenGuard],
				loadChildren: () =>
					import('@jepto/features/datawarehouse/datawarehouse.module').then(
						m => m.DatawarehouseModule
					),
			},
			{
				path: 'analysis',
				loadChildren: () =>
					import('@jepto/features/analysis/analysis.module').then(
						m => m.AnalysisModule
					),
			},
			{
				path: 'extensions',
				loadChildren: () =>
					import('@jepto/features/extensions/extension.module').then(
						m => m.ExtensionModule
					),
			},
			{
				path: 'local',
				loadChildren: () =>
					import('@jepto/features/local-listings/local-listings.module').then(
						m => m.LocalListingsModule
					),
			},
			{
				path: 'reviews',
				loadChildren: () =>
					import('@jepto/features/reviews/reviews.module').then(
						m => m.ReviewsModule
					),
			},
			{
				path: 'ai-visibility',
				loadChildren: () =>
					import('@jepto/features/ai-visibility/ai-visibility.routes').then(
						m => m.AI_VISIBILITY_ROUTES
					),
				canActivate: [AiVisibilityFeatureGuard],
			},
			{
				path: 'posts',
				loadChildren: () =>
					import('@jepto/features/social-posts/social-posts.module').then(
						m => m.SocialPostsModule
					),
			},
			{
				path: 'get-started',
				loadChildren: () =>
					import('@jepto/features/get-started/get-started.module').then(
						m => m.GetStartedModule
					),
			},
			{
				path: 'data-sources',
				loadChildren: () =>
					import('@jepto/features/data-sources/data-sources.routes').then(
						m => m.DATA_SOURCES_ROUTES
					),
			},
			{
				path: 'chats',
				loadChildren: () =>
					import('@jepto/features/datachat/datachat.routes').then(
						m => m.DATACHAT_ROUTES
					),
			},
			{
				path: 'settings',
				loadChildren: () =>
					import('@jepto/features/settings/settings.routes').then(
						m => m.SETTINGS_ROUTES
					),
			},
			{
				path: 'report',
				children: [
					{
						path: '',
						pathMatch: 'full',
						redirectTo: '/reports',
					},
					{
						path: 'palettes',
						pathMatch: 'full',
						redirectTo: '/reports/palettes',
					},
					{
						path: 'activity',
						pathMatch: 'full',
						redirectTo: '/reports/activity',
					},
					{
						path: 'templates',
						pathMatch: 'full',
						redirectTo: '/reports/templates',
					},
					{
						path: 'theme',
						pathMatch: 'full',
						redirectTo: '/reports/theme',
					},
					{
						path: 'branding',
						pathMatch: 'full',
						redirectTo: '/reports/branding',
					},
				],
			},
			{
				path: 'reports',
				loadChildren: () =>
					import('@jepto/features/reports/reports.routes').then(
						m => m.REPORTS_ROUTES
					),
			},
		],
	},
	{
		path: 'sign-up',
		loadChildren: () =>
			import('@jepto/features/signup/signup.module').then(
				m => m.SignUpModule
			),
	},
	{
		path: 'sign-in',
		loadChildren: () =>
			import('@jepto/features/signin/signin.module').then(
				m => m.SignInModule
			),
	},
	{
		path: 'change-password',
		loadChildren: () =>
			import('@jepto/features/forgot-password/forgot-password.module').then(
				m => m.ForgotPasswordModule
			),
	},
	{
		path: 'reset-password',
		loadChildren: () =>
			import('@jepto/features/reset-password/reset-password.module').then(
				m => m.ResetPasswordModule
			),
	},
	{
		path: 'create-password',
		loadChildren: () =>
			import('@jepto/features/create-password/create-password.module').then(
				m => m.CreatePasswordModule
			),
	},
	{
		path: 'subscribe',
		loadChildren: () =>
			import('@jepto/features/subscribe/subscribe.module').then(
				m => m.SubscribeModule
			),
		canActivate: [AuthGuard, SubscribeGuard],
	},
	{
		path: 'resubscribe',
		loadChildren: () =>
			import('@jepto/features/resubscribe/resubscribe.module').then(
				m => m.ResubscribeModule
			),
		canActivate: [AuthGuard, ResubscribeGuard],
	},
	{
		path: '**',
		redirectTo: '/sign-up',
		pathMatch: 'full',
	},
];

export const AppRouting: ModuleWithProviders<RouterModule> =
	RouterModule.forRoot(appRoutes, {
		preloadingStrategy: SelectivePreloadingStrategy,
		paramsInheritanceStrategy: 'always',
	});
